import React, { useEffect, useState } from 'react';
import { ApiRequest } from '@/utils/axios';
import QuestionList, { IFaqItem } from '@/components/Faq/QuestionList';
import SeoHead from '@/components/SEO/SeoHead';
import { socket } from '@/utils/hooks/use-subscription';

interface LeftBlock {
    title: string;
    description: string;
}

function splitParagraphs(text: string): string[] {
    return (text || '')
        .split(/\n+/)
        .map((s) => s.trim())
        .filter(Boolean);
}

function parseLeft(raw: string): LeftBlock[] {
    try {
        const parsed = JSON.parse(raw || '[]');
        if (!Array.isArray(parsed)) return [];
        return parsed.filter((b) => b && typeof b.title === 'string');
    } catch {
        return [];
    }
}

const ColTitle = ({ children }: { children: React.ReactNode }) => (
    <div className="flex items-center gap-2.5 px-1">
        <span
            className="w-1 h-[18px] rounded-sm shrink-0"
            style={{
                background: 'linear-gradient(#8b6dff,#3ad6ff)',
                boxShadow: '0 0 12px #8b6dff',
            }}
        />
        <span className="font-chat-nick text-[12px] font-extrabold tracking-[0.14em] uppercase text-text_3">
            {children}
        </span>
    </div>
);

const Faq = () => {
    const [questions, setQuestions] = useState<IFaqItem[]>([]);
    const [leftRaw, setLeftRaw] = useState<string>('[]');

    async function load() {
        const {
            data: { response },
        }: {
            data: {
                response: {
                    leftContent: string;
                    rightContent: IFaqItem[];
                };
            };
        } = await ApiRequest.get('/static-pages/faq');

        setQuestions(response.rightContent || []);
        setLeftRaw(response.leftContent || '[]');
    }

    useEffect(() => {
        load().then();

        const onUserUpdated = () => {
            load().then();
        };
        socket.on('userUpdated', onUserUpdated);
        return () => {
            socket.off('userUpdated', onUserUpdated);
        };
    }, []);

    const leftBlocks = parseLeft(leftRaw);

    return (
        <>
            <SeoHead category="FAQ" path="/faq" />
            <div className="flex flex-col gap-3 min-w-0 animate-fluent-fadeup">
                {/* Page header */}
                <section className="surface flex items-center gap-3.5 px-5 py-4">
                    <span
                        className="w-12 h-12 rounded-[28%] grid place-items-center shrink-0 text-white"
                        style={{
                            background: 'linear-gradient(135deg, #8b6dff, #3ad6ff)',
                            boxShadow:
                                'inset 0 1px 0 rgba(255,255,255,0.35), 0 0 24px -4px rgba(139,109,255,0.8)',
                        }}
                    >
                        <svg
                            viewBox="0 0 24 24"
                            className="w-6 h-6 stroke-current fill-none [stroke-width:2.2] [stroke-linecap:round] [stroke-linejoin:round]"
                        >
                            <path d="M9.2 9a2.8 2.8 0 1 1 4 2.5c-.9.5-1.7 1.1-1.7 2.2" />
                            <circle cx="11.5" cy="17.5" r="0.6" fill="currentColor" stroke="none" />
                        </svg>
                    </span>
                    <div className="flex flex-col leading-tight min-w-0">
                        <h1 className="font-chat-nick text-[18px] font-extrabold tracking-[0.08em] uppercase text-text_main">
                            Help Center
                        </h1>
                        <span className="font-chat-msg text-[13px] text-text_3">
                            Answers to common questions and our terms of service.
                        </span>
                    </div>
                </section>

                <div className="grid gap-3 items-start min-w-0 grid-cols-1 md:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
                    {/* Left: FAQ accordion (rightContent) */}
                    <section className="flex flex-col gap-2.5 min-w-0">
                        <ColTitle>Frequently asked questions</ColTitle>
                        <QuestionList items={questions} />
                    </section>

                    {/* Right: Terms of service (leftContent) as plain text */}
                    <section className="flex flex-col gap-2.5 min-w-0">
                        <ColTitle>Terms of service</ColTitle>
                        <div className="surface flex flex-col gap-6 px-6 py-6 max-md:px-5 max-md:py-5">
                            {leftBlocks.length > 0 ? (
                                leftBlocks.map((block, i) => {
                                    const paragraphs = splitParagraphs(block.description);
                                    return (
                                        <div key={i} className="flex flex-col gap-2.5">
                                            <h2 className="font-chat-nick flex items-center gap-2.5 text-[14px] font-extrabold tracking-[0.06em] uppercase text-text_main">
                                                <span
                                                    className="w-[3px] h-[15px] rounded-sm shrink-0"
                                                    style={{
                                                        background:
                                                            'linear-gradient(180deg,#8b6dff,#3ad6ff)',
                                                        boxShadow: '0 0 8px rgba(139,109,255,0.6)',
                                                    }}
                                                />
                                                {block.title}
                                            </h2>
                                            <div className="flex flex-col gap-2.5 pl-[15px]">
                                                {(paragraphs.length > 0
                                                    ? paragraphs
                                                    : [block.description]
                                                ).map((p, j) => (
                                                    <p
                                                        key={j}
                                                        className="font-chat-msg text-[13.5px] leading-[1.72] text-text_2 [text-wrap:pretty]"
                                                    >
                                                        {p}
                                                    </p>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="text-center text-text_3 text-sm py-4 font-chat-msg">
                                    No content yet
                                </div>
                            )}
                        </div>
                    </section>
                </div>
            </div>
        </>
    );
};

export default Faq;
